// WHY a day in an availability grid is empty.
//
// `/appointment/:doctorId/availableSlots` (and its ...WithSettings twin) stamp a
// `reason` on every empty day — see appointmentController's slot builders:
//   'closed'          no active WEEK RULE covers that date: the doctor has not
//                     opened that week for booking at all. This is the one that
//                     used to be invisible — a doctor with no open week saw a
//                     silently empty calendar and nothing mentioned week rules.
//   'no_availability' the week is open but the doctor keeps no clinic hours on
//                     that weekday (or none at the chosen site).
//   'past'            the day is behind us.
//   'full'            there were slots and they are all taken (or, for today,
//                     already started — the grid drops slots whose start passed).
//   'leave'           the doctor is on leave that day (P44).
//
// The slot builders stamp 'closed' and 'no_availability' themselves, and since
// 2026-09 they also stamp 'past' / 'full' on any other empty day. Note that
// 'closed' is stamped only on the patient-initiated path — the desk's own grid
// is deliberately not week-gated any more — so the week banner these labels
// feed appears for patients, not for staff.
//
// A DAY OF LEAVE (P44). As of 2026-09-15 the server has no reason of its own for
// leave: getLeaveDetails removes the day's slots, and the day then falls through
// to 'full', so a doctor who is simply away was labelled "Fully booked". The
// label is only as good as the reason, and this helper has nothing else to go
// on (the grids that call it do not hold the doctor's leave dates), so the fix
// is split: 'leave' is understood here and reads "On leave", and the slot
// builders need to stamp `reason: 'leave'` on a day getLeaveDetails blocks
// (appointmentController.js — backend change reported, not made here). Until
// they do, a leave day still arrives as 'full'.
//
// Without this the grid says one flat "No times" for all four. Wording is kept
// identical to Frontend-patient-web's booking page (src/pages/BookAppointment.jsx)
// so a patient and the desk describe the same doctor's diary the same way.
//
// Defaults are passed to `t` so the labels render before anyone adds the keys to
// public/locales/{en,fr}.json.

// Local calendar day as YYYY-MM-DD (never UTC — the grid is drawn in local time).
const localDay = (d) => {
    const dt = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

/**
 * Label for an empty day in the availability grid.
 * @param {string|undefined} reason the server's per-day `reason`
 * @param {Date|string|undefined} date that day, for the past/today comparison
 * @param {Function} t i18next translate
 * @param {Date} [today] injectable "now" (tests)
 */
export const emptyDayLabel = (reason, date, t, today = new Date()) => {
    const tr = typeof t === 'function' ? t : (_k, d) => d;
    const dayKey = date ? localDay(date) : '';
    const todayKey = localDay(today);

    if (reason === 'past' || (dayKey && todayKey && dayKey < todayKey)) {
        return tr('slotreason.past', 'Past');
    }
    if (reason === 'closed') return tr('slotreason.closed', 'Not open yet');
    if (reason === 'no_availability') return tr('slotreason.no_availability', 'No clinic day');
    // Checked before 'full': a doctor who is away is not a doctor whose diary
    // is taken, and telling a patient "Fully booked" sends them hunting for a
    // cancellation that will never come.
    if (reason === 'leave') return tr('slotreason.leave', 'On leave');
    if (reason === 'full') {
        // Today with nothing left is usually the CLOCK, not the diary — the grid
        // drops every slot that already started — so it is not called "booked".
        return dayKey && todayKey && dayKey === todayKey
            ? tr('slotreason.none_left_today', 'No times left today')
            : tr('slotreason.full', 'Fully booked');
    }
    return tr('slotreason.no_times', 'No times');
};

/**
 * Longer explanation for a day the doctor has not opened for booking. Shown as a
 * tooltip beside the short label so the desk knows where to go and fix it.
 */
export const emptyDayHint = (reason, t) => {
    const tr = typeof t === 'function' ? t : (_k, d) => d;
    if (reason === 'closed') {
        return tr('slotreason.closed_hint', 'This week is not open for booking yet — open it under the doctor’s availability week rules.');
    }
    if (reason === 'no_availability') {
        return tr('slotreason.no_availability_hint', 'The doctor keeps no clinic hours on this day at the selected site.');
    }
    if (reason === 'leave') {
        return tr('slotreason.leave_hint', 'The doctor is on leave this day — no appointments can be booked.');
    }
    return '';
};

export default emptyDayLabel;
